import { REGIONS } from 'v2/configs/constants'
import type { ROrganizationLite } from 'v2/types/api/settings'

export interface FreeTrialRegionLock {
  atLimit: boolean
  lockedRegion: string
  currentRegion: string
}

/**
 * Free trial gets one organization per data region (hodor
 * `MaxOrgsPerRegionByPlan`), so a trial admin is eligible to create an org in
 * whichever region they do not occupy yet — a US org alongside their India one
 * is allowed, and that is the point of SER-6631. The lock the modal needs is
 * therefore "which region may this be" plus "is that slot already used", never
 * "is this a trial", which is why no caller should hide its create action on
 * `isFreePlan` alone.
 *
 * `targetRegion` pins the answer when the caller already knows which region
 * unblocks the user (the buy-number blocker, the outside-India plans band).
 * Without it, the free region is chosen, preferring the one opposite the org
 * they are in.
 *
 * Paid plans carry no per-region lock; their limits are hodor's to enforce.
 */
export const resolveFreeTrialRegionLock = (
  organizations: ROrganizationLite[],
  isFreePlan: boolean,
  currentRegion: string,
  targetRegion?: string,
): FreeTrialRegionLock | undefined => {
  if (!isFreePlan) return undefined

  const occupiedRegions = new Set(organizations.map((org) => org.region))
  if (targetRegion) {
    return { atLimit: occupiedRegions.has(targetRegion), lockedRegion: targetRegion, currentRegion }
  }

  const oppositeRegion = currentRegion === REGIONS.IN ? REGIONS.US : REGIONS.IN
  const freeRegions = [REGIONS.US, REGIONS.IN].filter((region) => !occupiedRegions.has(region))
  return {
    atLimit: freeRegions.length === 0,
    lockedRegion: freeRegions.includes(oppositeRegion)
      ? oppositeRegion
      : (freeRegions[0] ?? oppositeRegion),
    currentRegion,
  }
}
